// Users Table Component - Following Tasks Table Pattern

import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Search,
  X,
  Users as UsersIcon,
  Shield,
  Code,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';

import { formatDistanceToNow } from 'date-fns';
import type { User, Team, UserFilters } from '@/types/teams';
import { UserDetailPanel } from './UserDetailPanel';
import { ManageTeamsModal } from './ManageTeamsModal';

// Helper function to get user initials
const getUserInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to generate a consistent color based on user name
const getUserColor = (name: string): string => {
  const colors = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Orange
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#14B8A6', // Teal
    '#EC4899', // Pink
    '#F97316', // Orange
    '#84CC16', // Lime
    '#06B6D4', // Cyan
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: UsersIcon,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface UsersTableProps {
  users: User[];
  teams: Team[];
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
  onRefresh: () => void;
  onRefreshUsers?: () => Promise<{ totalCount: number; newUsers: number; updatedUsers: number; syncedAt: string } | null>;
  refreshing?: boolean;
}

export function UsersTable({
  users,
  teams,
  filters,
  onFiltersChange,
  onRefresh,
  onRefreshUsers,
  refreshing = false,
}: Readonly<UsersTableProps>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showManageTeamsModal, setShowManageTeamsModal] = useState(false);
  const [bulkManageUsers, setBulkManageUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Filter users
  const filteredUsers = useMemo(() => {
    let result = [...users];

    // Apply team filter
    if (filters.teamId) {
      result = result.filter(u => u.teams.includes(filters.teamId!));
    }

    // Apply status filter
    if (filters.status === 'active') {
      result = result.filter(u => u.status === 'active');
    } else if (filters.status === 'inactive') {
      result = result.filter(u => u.status === 'inactive');
    }

    // Apply multi-team filter
    if (filters.multiTeam === 'multiple') {
      result = result.filter(u => u.teams.length > 1);
    } else if (filters.multiTeam === 'single') {
      result = result.filter(u => u.teams.length === 1);
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        u =>
          u.name.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query)
      );
    }

    // Sort by name
    result.sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }, [users, filters, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [filteredUsers.length]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.teamId) count++;
    if (filters.status && filters.status !== 'all') count++;
    if (filters.multiTeam) count++;
    return count;
  }, [filters]);

  const clearFilters = () => {
    onFiltersChange({});
    setSearchQuery('');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(filteredUsers.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    }
  };

  const handleBulkManageTeams = () => {
    const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
    setBulkManageUsers(selectedUsers);
    setShowManageTeamsModal(true);
  };

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
  };

  const getTeamById = (teamId: string) => {
    return teams.find(t => t.id === teamId);
  };

  const getUserTeamBadges = (user: User) => {
    const userTeams = user.teams.map(team => {
      const teamId = typeof team === 'string' ? team : team.teamId;
      return getTeamById(teamId);
    }).filter(Boolean) as Team[];
    
    // Show max 3 teams, then +X badge if more than 3 total
    const maxVisible = 3;
    const visibleTeams = userTeams.slice(0, maxVisible);
    const remainingCount = userTeams.length > maxVisible ? userTeams.length - maxVisible : 0;

    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visibleTeams.map(team => {
          const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
          return (
            <Badge
              key={team.id}
              variant="outline"
              className="gap-1"
              style={{
                backgroundColor: `${team.color}10`,
                borderColor: team.color,
                color: team.color,
              }}
            >
              <IconComponent className="h-3 w-3" />
              {team.name}
            </Badge>
          );
        })}
        {remainingCount > 0 && (
          <Badge variant="secondary">+{remainingCount}</Badge>
        )}
        {userTeams.length === 0 && (
          <Badge variant="destructive">Unassigned</Badge>
        )}
      </div>
    );
  };

    // Get unique teams from filtered users for filter panel
    const _availableTeams = useMemo(() => {
      const teamIds = new Set<string>();
      filteredUsers.forEach(user => {
        user.teams.forEach(team => {
          const teamId = typeof team === 'string' ? team : team.teamId;
          teamIds.add(teamId);
        });
      });
      return teams.filter(t => teamIds.has(t.id));
    }, [filteredUsers, teams]);

  return (
    <>
      <div className="flex-1 flex overflow-hidden h-full">
        {/* Left Filter Panel */}
        <div className="w-64 border-r bg-muted/10 flex flex-col h-full">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm">Filters</h3>
          </div>

          <ScrollArea className="flex-1 h-full">
            <div className="p-4 space-y-6">
              {/* Team Assignment */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                  Team Assignment
                </h4>
                <div className="space-y-1">
                  <Button
                    variant={!filters.teamId ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, teamId: undefined })}
                  >
                    All Users
                  </Button>
                  
                  {/* Default Teams */}
                  {teams.filter(t => t.isDefault).map(team => {
                    const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
                    return (
                      <Button
                        key={team.id}
                        variant={filters.teamId === team.id ? 'secondary' : 'ghost'}
                        size="sm"
                        className="w-full justify-start gap-2 font-normal"
                        onClick={() => onFiltersChange({ ...filters, teamId: filters.teamId === team.id ? undefined : team.id })}
                      >
                        <span style={{ color: team.color }}><IconComponent className="h-4 w-4" /></span>
                        <span className="flex-1 text-left">{team.name}</span>
                        <span className="text-xs text-muted-foreground">{team.memberCount}</span>
                      </Button>
                    );
                  })}
                  
                  {/* Divider */}
                  <div className="border-t my-2" />
                  
                  {/* Custom Teams */}
                  {teams.filter(t => !t.isDefault).map(team => {
                    const IconComponent = TEAM_ICONS[team.icon] || UsersIcon;
                    return (
                      <Button
                        key={team.id}
                        variant={filters.teamId === team.id ? 'secondary' : 'ghost'}
                        size="sm"
                        className="w-full justify-start gap-2 font-normal"
                        onClick={() => onFiltersChange({ ...filters, teamId: filters.teamId === team.id ? undefined : team.id })}
                      >
                        <span style={{ color: team.color }}><IconComponent className="h-4 w-4" /></span>
                        <span className="flex-1 text-left">{team.name}</span>
                        <span className="text-xs text-muted-foreground">{team.memberCount}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                  Status
                </h4>
                <div className="space-y-1">
                  <Button
                    variant={!filters.status || filters.status === 'all' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, status: 'all' })}
                  >
                    All
                  </Button>
                  <Button
                    variant={filters.status === 'active' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, status: filters.status === 'active' ? undefined : 'active' })}
                  >
                    Active
                  </Button>
                  <Button
                    variant={filters.status === 'inactive' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, status: filters.status === 'inactive' ? undefined : 'inactive' })}
                  >
                    Inactive
                  </Button>
                </div>
              </div>

              {/* Multi-Team Users */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                  Multi-Team Users
                </h4>
                <div className="space-y-1">
                  <Button
                    variant={filters.multiTeam === 'multiple' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, multiTeam: filters.multiTeam === 'multiple' ? undefined : 'multiple' })}
                  >
                    In Multiple Teams
                  </Button>
                  <Button
                    variant={filters.multiTeam === 'single' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => onFiltersChange({ ...filters, multiTeam: filters.multiTeam === 'single' ? undefined : 'single' })}
                  >
                    In Single Team Only
                  </Button>
                </div>
              </div>
            </div>
          </ScrollArea>

          {activeFilterCount > 0 && (
            <div className="p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={clearFilters}
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search and Actions Bar */}
          <div className="p-4 border-b flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {onRefreshUsers && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const result = await onRefreshUsers();
                  if (result) {
                    onRefresh();
                  }
                }}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Syncing...' : 'Sync Users'}
              </Button>
            )}
          </div>

          {/* Bulk Action Toolbar */}
          {selectedUserIds.length > 0 && (
            <div className="px-4 py-3 border-b bg-primary/5 border-primary/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedUserIds.length} users selected</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleBulkManageTeams}>
                  Manage Teams
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedUserIds([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <ScrollArea className="flex-1">
              <div className="overflow-x-auto">
                <Table>
                <TableHeader className="sticky top-0 bg-muted z-10">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="w-[30%]">Name</TableHead>
                    <TableHead className="w-[25%]">Email</TableHead>
                    <TableHead className="w-[30%]">Teams</TableHead>
                    <TableHead className="w-[15%]">Last Login</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-white dark:bg-background">
                  {paginatedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                        No users found matching your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleUserClick(user)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-md flex items-center justify-center text-xs font-semibold text-white"
                            style={{ backgroundColor: getUserColor(user.name) }}
                          >
                            {getUserInitials(user.name)}
                          </div>
                          <span className="font-medium text-primary hover:underline">
                            {user.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        {getUserTeamBadges(user)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {user.lastLogin
                          ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true })
                          : 'Never'}
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </ScrollArea>

            {/* Pagination Footer */}
            <div className="p-4 border-t flex items-center justify-between bg-white dark:bg-background">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Detail Panel */}
      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          teams={teams}
          onClose={() => setSelectedUser(null)}
          onUpdate={onRefresh}
        />
      )}

      {/* Manage Teams Modal */}
      {showManageTeamsModal && (
        <ManageTeamsModal
          users={bulkManageUsers}
          teams={teams}
          onClose={() => {
            setShowManageTeamsModal(false);
            setBulkManageUsers([]);
            setSelectedUserIds([]);
          }}
          onSave={onRefresh}
        />
      )}
    </>
  );
}
